import { getColor } from "../../../../utils/getColor";

const getOrCreateTooltip = (chart, tooltip) => {
	const tooltipEl = chart.canvas.parentNode.querySelector("div.tooltip");

	if (tooltipEl) {
		return tooltipEl;
	} else {
		// otherwise create a new element to use for tooltip
		const newTooltipEl = document.createElement("div");
		newTooltipEl.classList.add("tooltip");
		newTooltipEl.style.background = getColor("neutral", 7);
		newTooltipEl.style.borderRadius = "4px";
		newTooltipEl.style.color = "white";
		newTooltipEl.style.pointerEvents = "none";
		newTooltipEl.style.position = "absolute";
		newTooltipEl.style.zIndex = "1";

		// position tooltip with pointer in the middle and 8px below the cursor to leave room for carat
		newTooltipEl.style.transform = "translate(-50%, 8px)";

		// make invisible at first
		newTooltipEl.style.opacity = "0";

		// match animation speed of chart.js tooltip
		newTooltipEl.style.transition = "all .3s ease-in-out, opacity .2s linear";

		const table = document.createElement("table");
		table.style.margin = "0px";

		newTooltipEl.appendChild(table);
		chart.canvas.parentNode.appendChild(newTooltipEl);

		// create carat for tooltip that is pointing to the data point
		// and color should match the tooltip background color
		const carat = document.createElement("div");
		carat.style.position = "absolute";
		carat.style.border = "none";

		// set the carat to be the same color as the tooltip background
		carat.style.background = newTooltipEl.style.background;

		// shape the div not the border into a triangle pointing up
		carat.style.width = "8px";
		carat.style.height = "8px";

		// move it to the top edge of the tooltip
		carat.style.top = `-${tooltip.options.padding - 2}px`;

		// move it to the center
		carat.style.left = `calc(50% - ${tooltip.options.padding}px)`;

		// rotate and clip the bottom of the carat
		carat.style.transform = "rotate(45deg)";
		carat.style.clipPath = "polygon(0 0, 100% 0, 0 100%)";

		// append the carat to the tooltip
		newTooltipEl.appendChild(carat);

		return newTooltipEl;
	}
};

const createExternalTooltip = (context, labels, getPointColor) => {
	// Tooltip Element
	const { chart, tooltip } = context;

	const tooltipEl = getOrCreateTooltip(chart, tooltip);

	// Hide if no tooltip
	if (tooltip.opacity === 0) {
		tooltipEl.style.opacity = 0;
		return;
	}

	// Set Text
	if (tooltip.body) {
		const titleLines = tooltip.title || [];
		const bodyLines = tooltip.body.map((b) => b.lines);

		const tableHead = document.createElement("thead");

		titleLines.forEach((title) => {
			const tr = document.createElement("tr");
			tr.style.borderWidth = "0";

			const th = document.createElement("th");
			th.style.borderWidth = "0";
			const text = document.createTextNode(title);

			th.appendChild(text);
			tr.appendChild(th);
			tableHead.appendChild(tr);
		});

		const tableBody = document.createElement("tbody");
		bodyLines.forEach((body: string[], inx) => {
			// limit to only 5 lines per tooltip
			if (inx > 4) return;

			// create color dot
			const span = document.createElement("span");
			// get the color for the data point from the context.tooltip `raw` property, which includes `eNPSScore` key
			span.style.backgroundColor = getPointColor(context.tooltip.dataPoints[inx].raw);
			span.style.borderWidth = "0";
			span.style.borderRadius = "50%";
			span.style.marginRight = "4px";
			span.style.height = "8px";
			span.style.width = "8px";

			const tr = document.createElement("tr");
			tr.style.backgroundColor = "inherit";
			tr.style.borderWidth = "0";

			const td = document.createElement("td");
			td.style.borderWidth = "0";
			td.style.display = "flex";
			td.style.alignItems = "center";
			td.style.whiteSpace = "nowrap";
			td.style.height = "16px";

			const dataPoints = body[0].replace(/[(|)]+/, "").split(",");

			const axisLabel = (label: string) => `<span style="font-weight:400;">${label}</span>`;
			const xAxis = +parseFloat(dataPoints[0]).toFixed(1);
			const yAxis = +parseFloat(dataPoints[1]).toFixed(1);

			const tooltipLineHTML = `<span>${axisLabel(
				labels.xAxisLabel
			)}:&nbsp;${xAxis},</span><span>&nbsp;${axisLabel(labels.yAxisLabel)}:&nbsp;${yAxis}</span>`;

			const text = document.createElement("span");
			text.style.fontSize = "12px";
			text.style.display = "flex";
			text.style.alignItems = "center";
			text.style.justifyContent = "space-between";
			text.style.width = "100%";
			text.style.lineHeight = "16px";
			text.style.verticalAlign = "center";
			text.innerHTML = tooltipLineHTML;

			td.appendChild(span);
			td.appendChild(text);
			tr.appendChild(td);
			tableBody.appendChild(tr);
		});

		const tableRoot = tooltipEl.querySelector("table");

		// Remove old children
		while (tableRoot.firstChild) {
			tableRoot.firstChild.remove();
		}

		// Add new children
		tableRoot.appendChild(tableHead);
		tableRoot.appendChild(tableBody);
	}

	const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

	// Display, position, and set styles for font
	tooltipEl.style.opacity = 1;
	tooltipEl.style.left = positionX + tooltip.caretX + "px";
	tooltipEl.style.top = positionY + tooltip.caretY + "px";
	tooltipEl.style.font = "Source Sans Pro, sans-serif";
	tooltipEl.style.padding = tooltip.options.padding + "px " + tooltip.options.padding + "px";
};

export default createExternalTooltip;
